import { Roles } from "../../constants/roles";

import { FormBase } from "../../util/form-base-form";

export class PostShowForm extends FormBase {
    public getElementModal(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL}"]`, HTMLElement);
    }

    public getElementModalOverlay(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getClosestElementModal(element: Element | null | undefined): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.ELEMENT_MODAL}"]`, HTMLElement);
    }

    public getElementModalBody(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement);
    }

    public isInModalContext(element: Element | null | undefined): boolean {
        return !!(
            element instanceof Element &&
            this.closest(element, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement)
        );
    }

    public getPostDetail(root: ParentNode = document, postId?: number): HTMLElement | null {
        const selector = postId
            ? `[data-role="${Roles.POST_DETAIL}"][data-post-id="${String(postId)}"]`
            : `[data-role="${Roles.POST_DETAIL}"][data-post-id]`;
        return this.queryElement(root, selector, HTMLElement);
    }

    public getHiddenPostDetail(root: ParentNode = document, postId?: number): HTMLElement | null {
        const selector = postId
            ? `[data-role="${Roles.POST_DETAIL}"][data-hidden-role="${Roles.POST_DETAIL_HIDDEN}"][data-post-id="${String(postId)}"]`
            : `[data-role="${Roles.POST_DETAIL}"][data-hidden-role="${Roles.POST_DETAIL_HIDDEN}"][data-post-id]`;
        return this.queryElement(root, selector, HTMLElement);
    }

    public getClosestPostDetail(element: Element | null | undefined): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.POST_DETAIL}"]`, HTMLElement);
    }

    public getClosestBoardForm(element: Element | null | undefined): HTMLFormElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, "form[data-board-id]", HTMLFormElement);
    }

    public getPostCard(root: ParentNode = document, postId?: number): HTMLElement | null {
        const selector = postId
            ? `[data-role="${Roles.POST_CARD}"][data-post-id="${String(postId)}"]`
            : `[data-role="${Roles.POST_CARD}"][data-post-id]`;
        return this.queryElement(root, selector, HTMLElement);
    }

    public getClosestPostCard(element: Element | null | undefined): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.POST_CARD}"]`, HTMLElement);
    }

    public getContextPostDetail(postId: number, contextElement: Element | null | undefined): HTMLElement | null {
        if (this.isInModalContext(contextElement)) {
            const modalBody = this.getElementModalBody();
            return modalBody ? this.getPostDetail(modalBody, postId) : null;
        }

        return this.getPostDetail(document, postId);
    }

    public getEditFormContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_FORM}"]`, HTMLElement);
    }

    public getEditFormInner(root: ParentNode): HTMLFormElement | null {
        return this.getForm(root, `form[data-role="${Roles.POST_EDIT_FORM_INNER}"]`);
    }

    public getTitleInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="post_title"]');
    }

    public getContentTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.queryElement(root, 'textarea[name="content"]', HTMLTextAreaElement);
    }

    public getVideoUploadInput(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(root, `[data-role="${Roles.VIDEO_UPLOAD}"]`, HTMLInputElement);
    }

    public getRemovedAttachedVideosInput(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(root, 'input[name="removed_attached_videos"]', HTMLInputElement);
    }

    public getRemovedAttachedImagesInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="removed_attached_images"]');
    }

    public getMediaRemovedIndicator(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MEDIA_REMOVED_INDICATOR}"]`, HTMLElement);
    }

    public getMediaControls(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MEDIA_CONTROLS}"]`, HTMLElement);
    }

    public getImageGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.IMAGE_GRID}"]`, HTMLElement);
    }

    public getVideoGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.VIDEO_GRID}"]`, HTMLElement);
    }

    public getFileInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[type="file"]');
    }

    public getBackNumberToggle(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[type="checkbox"][data-back-number-toggle="1"]');
    }

    public getCreatedAtInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[type="date"][name="created_at"][data-back-number-date="1"]');
    }

    public getProductIdInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, `[data-role="${Roles.POST_PRODUCT_ID}"]`);
    }

    public getPostStatusSelect(root: ParentNode): HTMLSelectElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_STATUS_SELECT}"]`, HTMLSelectElement);
    }

    public getClosestElementModalOverlay(element: Element | null | undefined): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.ELEMENT_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getPostProductPreview(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PRODUCT_PREVIEW}"]`, HTMLElement);
    }

    public getEditForms(postId: number, root: ParentNode = document): HTMLFormElement[] {
        return this.queryElements(
            root,
            `form[data-role="${Roles.POST_EDIT_FORM_INNER}"][data-edit-form="${String(postId)}"]`,
            HTMLFormElement,
        );
    }

    public getHiddenSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, 'input[type="hidden"][name="sns_links[]"]', HTMLInputElement);
    }

    public getAllSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, 'input[name="sns_links[]"]', HTMLInputElement);
    }

    public getPostDetailContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DETAIL_CONTENT}"]`, HTMLElement);
    }

    public getPostDetailHeader(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DETAIL_HEADER}"]`, HTMLElement);
    }

    public getPostDetailTitle(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DETAIL_TITLE}"]`, HTMLElement);
    }

    public getPostAuthorName(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_AUTHOR_NAME}"]`, HTMLElement);
    }

    public getPostCreatedAt(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CREATED_AT}"]`, HTMLElement);
    }

    public getPostDropdownContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DROPDOWN_CONTAINER}"]`, HTMLElement);
    }

    public getPostProductLinkerViews(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.POST_PRODUCT_LINKER_VIEW}"]`, HTMLElement);
    }

    public getPostAttachmentsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_ATTACHMENTS_SECTION}"]`, HTMLElement);
    }

    public getPostDetailMediaSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-detail-media-section", HTMLElement);
    }

    public getPostDetailUserProfile(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-detail-user-profile", HTMLElement);
    }

    public getCommentsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENTS_SECTION}"]`, HTMLElement);
    }

    public getPostAttachmentBlock(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_ATTACHMENT_BLOCK}"]`, HTMLElement);
    }

    public getPostAttachmentContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_ATTACHMENT_CONTENT}"]`, HTMLElement);
    }

    public getPostAttachmentImageItems(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.POST_ATTACHMENT_IMAGE_ITEM}"]`, HTMLElement);
    }

    public getPostAttachmentImages(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, `[data-role="${Roles.POST_ATTACHMENT_IMAGE}"]`, HTMLImageElement);
    }

    public getClosestPostAttachmentImageItem(element: Element | null | undefined): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.POST_ATTACHMENT_IMAGE_ITEM}"]`, HTMLElement);
    }

    public getPostAttachmentVideoItem(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_ATTACHMENT_VIDEO_ITEM}"]`, HTMLElement);
    }

    public getSnsLinksData(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_SNS_LINKS_DATA}"]`, HTMLElement);
    }

    public getImageManager(root: ParentNode, postId?: number): HTMLElement | null {
        const selector = postId
            ? `[data-role="${Roles.IMAGE_MANAGER}"][data-post-id="${String(postId)}"]`
            : `[data-role="${Roles.IMAGE_MANAGER}"]`;
        return this.queryElement(root, selector, HTMLElement);
    }

    public getVideoManager(root: ParentNode, postId?: number): HTMLElement | null {
        const selector = postId
            ? `[data-role="${Roles.VIDEO_MANAGER}"][data-post-id="${String(postId)}"]`
            : `[data-role="${Roles.VIDEO_MANAGER}"]`;
        return this.queryElement(root, selector, HTMLElement);
    }

    public getExistingImageTiles(root: ParentNode): HTMLElement[] {
        return this.queryElements(
            root,
            `[data-item-role="${Roles.EXISTING_IMAGE_TILE}"][data-image-type="existing"]`,
            HTMLElement,
        );
    }

    public getExistingImageFilenames(root: ParentNode): string[] {
        return this.getExistingImageTiles(root)
            .map((tile) => String(tile.dataset.imageFilename || "").trim())
            .filter((filename) => filename !== "");
    }

    public getExistingVideoTiles(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, '[data-video-type="existing"]', HTMLElement);
    }

    public getExistingVideoFilenames(root: ParentNode): string[] {
        return this.getExistingVideoTiles(root)
            .map((tile) => String(tile.dataset.videoFilename || "").trim())
            .filter((filename) => filename !== "");
    }

    public getNewVideoTiles(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, '[data-video-type="new"]', HTMLElement);
    }

    public getBoardIdMeta(root: ParentNode = document): HTMLMetaElement | null {
        return this.queryElement(root, 'meta[name="board-id"]', HTMLMetaElement);
    }

    public getUserIdMeta(root: ParentNode = document): HTMLMetaElement | null {
        return this.queryElement(root, 'meta[name="user-id"]', HTMLMetaElement);
    }

    public getPickerBody(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.PICKER_BODY}"]`, HTMLElement);
    }

    public getWysiwygEditor(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_EDITOR}"]`, HTMLElement);
    }

    public getVisibleSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement).filter(
            (element): element is HTMLInputElement =>
                element instanceof HTMLInputElement && element.type === "text" && !element.disabled,
        );
    }

    public getClosestElement(target: EventTarget | Element | null | undefined, selector: string): HTMLElement | null {
        return this.closest(target, selector, HTMLElement);
    }

    public getMenuButtonFromClosestDropdown(element: Element | null | undefined): HTMLButtonElement | null {
        const dropdown = this.closest(element, ".post-dropdown", HTMLElement);
        if (!dropdown) return null;
        return this.queryElement(dropdown, `[data-role="${Roles.POST_MENU_BUTTON}"]`, HTMLButtonElement);
    }

    public getWrappedImages(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, "img", HTMLImageElement);
    }
}

export const postShowForm = new PostShowForm();
